import { DateTime } from 'luxon';

/**
 * All timestamps are stored in UTC. These helpers convert to the outlet/tenant timezone
 * for business-day, financial-year and "end of day" calculations.
 */

export function isValidTimezone(tz) {
  return typeof tz === 'string' && tz.length > 0 && DateTime.local().setZone(tz).isValid;
}

/** 'YYYY-MM-DD' in `tz` → UTC Date of 23:59:59.999 local time on that date. */
export function endOfDayInZone(dateStr, tz) {
  const dt = DateTime.fromISO(dateStr, { zone: tz });
  if (!dt.isValid) throw new Error(`Invalid date "${dateStr}" for zone "${tz}"`);
  return dt.endOf('day').toJSDate();
}

/** 'YYYY-MM-DD' in `tz` → UTC Date of 00:00 local time on that date. */
export function startOfDayInZone(dateStr, tz) {
  const dt = DateTime.fromISO(dateStr, { zone: tz });
  if (!dt.isValid) throw new Error(`Invalid date "${dateStr}" for zone "${tz}"`);
  return dt.startOf('day').toJSDate();
}

/** Date → 'YYYY-MM-DD' as seen in `tz`. */
export function toZoneDateString(date, tz) {
  return DateTime.fromJSDate(new Date(date)).setZone(tz).toISODate();
}

/**
 * The trading day a moment belongs to. With a 04:00 cut-off, a bill at 01:30 on the 6th
 * belongs to business day the 5th.
 */
export function businessDate(date, tz, cutoffMinutes = 240) {
  return DateTime.fromJSDate(new Date(date)).setZone(tz).minus({ minutes: cutoffMinutes }).toISODate();
}

/**
 * Indian financial year (April–March) of the *calendar* date in `tz` (GST invoice date).
 * 2026-10-05 → { startYear: 2026, endYear: 2027, code: '2627', label: '2026-27' }
 */
export function financialYear(date, tz) {
  const dt = DateTime.fromJSDate(new Date(date)).setZone(tz);
  const startYear = dt.month >= 4 ? dt.year : dt.year - 1;
  const endYear = startYear + 1;
  return {
    startYear,
    endYear,
    code: `${String(startYear).slice(2)}${String(endYear).slice(2)}`,
    label: `${startYear}-${String(endYear).slice(2)}`,
  };
}

/** Whole calendar days from `from` to `to`, comparing local dates in `tz` (can be negative). */
export function calendarDaysBetween(from, to, tz) {
  const a = DateTime.fromJSDate(new Date(from)).setZone(tz).startOf('day');
  const b = DateTime.fromJSDate(new Date(to)).setZone(tz).startOf('day');
  return Math.round(b.diff(a, 'days').days);
}

export function addDays(date, days) {
  return new Date(new Date(date).getTime() + days * 86_400_000);
}

/** Add calendar months in `tz`, keeping end-of-day semantics. */
export function addMonthsEndOfDay(date, months, tz) {
  return DateTime.fromJSDate(new Date(date)).setZone(tz).plus({ months }).endOf('day').toJSDate();
}

export function formatInZone(date, tz, format = 'dd LLL yyyy') {
  if (!date) return '';
  return DateTime.fromJSDate(new Date(date)).setZone(tz).toFormat(format);
}

/** Minutes `tz` is ahead of UTC at `date` (e.g. 330 for Asia/Kolkata). */
export function zoneOffsetMinutes(date, tz) {
  return DateTime.fromJSDate(new Date(date)).setZone(tz).offset;
}

/**
 * The wall-clock time in `tz` as a Date whose UTC fields show it (for spreadsheets, which have
 * no time zones). Offsets are looked up once per hour of time (they only change on the hour).
 */
export function wallClockConverter(tz) {
  const offsets = new Map();
  return (date) => {
    const ms = new Date(date).getTime();
    const hour = Math.floor(ms / 3_600_000);
    let offset = offsets.get(hour);
    if (offset === undefined) {
      offset = zoneOffsetMinutes(ms, tz);
      offsets.set(hour, offset);
    }
    return new Date(ms + offset * 60_000);
  };
}
